import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { fetchPublicBureau, fetchPublicEvents, type PublicBureauMember, type PublicEventItem } from "@/features/public/api";
import { formatEventDateTimeRange } from "@/shared/events/dateRange";
import { useRealtimeInvalidation } from "@/shared/realtime/useRealtimeInvalidation";

function getMemberInitials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((chunk) => chunk[0]?.toUpperCase() ?? "")
    .join("");
}

function toMeetingTimestamp(meeting: PublicEventItem) {
  return new Date(`${meeting.date}T${meeting.heure}:00`).getTime();
}

export default function BureauSection() {
  const [visible, setVisible] = useState(false);
  const [bureauMembers, setBureauMembers] = useState<PublicBureauMember[]>([]);
  const [nextBureauMeeting, setNextBureauMeeting] = useState<PublicEventItem | null>(null);
  const [meetingLoading, setMeetingLoading] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const bureauHighlights = bureauMembers.slice(0, 4);

  const loadBureau = useCallback(async () => {
    try {
      const data = await fetchPublicBureau();
      setBureauMembers(data);
    } catch {
      setBureauMembers([]);
    }
  }, []);

  const loadBureauMeeting = useCallback(async () => {
    setMeetingLoading(true);

    try {
      const data = await fetchPublicEvents({
        type: "BUREAU_MEETING",
        status: "PUBLISHED",
        page: 1,
        limit: 50
      });
      const now = Date.now();
      const meeting = data.items
        .filter((item) => toMeetingTimestamp(item) >= now)
        .sort((left, right) => toMeetingTimestamp(left) - toMeetingTimestamp(right))[0] ?? null;

      setNextBureauMeeting(meeting);
    } catch {
      setNextBureauMeeting(null);
    } finally {
      setMeetingLoading(false);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    void loadBureau();
    void loadBureauMeeting();
  }, [loadBureau, loadBureauMeeting]);

  useRealtimeInvalidation({
    topics: ["bureau", "events"],
    onInvalidate: () => {
      void loadBureau();
      void loadBureauMeeting();
    }
  });

  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className={`transition-all duration-700 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <span className="inline-block px-4 py-1.5 rounded-full bg-teal-50 text-[#1A8C7A] text-sm font-medium mb-4">
              Le Club
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-6">
              Notre Bureau
            </h2>
            <p className="text-lg text-gray-500 leading-relaxed mb-8">
              L'ASC Sonatel est dirigée par un bureau élu composé de membres dévoués qui œuvrent pour le développement sportif et culturel de tous les salariés de la SONATEL.
            </p>
            {bureauHighlights.length > 0 ? (
              <div className="grid grid-cols-2 gap-4 mb-10">
                {bureauHighlights.map((member) => (
                  <div key={member.id} className="flex items-center gap-3 p-4 rounded-2xl bg-[#FAF9F7]">
                    <div className="w-11 h-11 flex items-center justify-center rounded-full bg-gradient-to-br from-[#E8521A] via-[#f97316] to-[#1A8C7A] text-white flex-shrink-0 font-bold text-xs tracking-wide">
                      {member.avatar ? (
                        <img src={member.avatar} alt={member.nom} className="w-full h-full object-cover rounded-full" />
                      ) : (
                        getMemberInitials(member.nom)
                      )}
                    </div>
                    <div>
                      <div className="text-sm text-gray-400 line-clamp-2">{member.role}</div>
                      <div className="text-sm font-semibold text-[#1A1A1A] line-clamp-2">{member.nom}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mb-10 rounded-2xl border border-dashed border-gray-200 bg-[#FAF9F7] px-5 py-6 text-sm text-gray-500">
                Les quatre premiers membres du bureau créés par l&apos;admin apparaîtront ici automatiquement.
              </div>
            )}
            <Link
              to="/club?tab=bureau"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#1A8C7A] text-white font-semibold hover:bg-[#157a6a] transition-all duration-200 whitespace-nowrap"
            >
              <i className="ri-information-line"></i>
              Voir tous les membres
            </Link>
          </div>

          <div className={`relative transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <div className="relative rounded-3xl overflow-hidden" style={{ height: "480px" }}>
              <img
                src="https://readdy.ai/api/search-image?query=african%20sports%20club%20committee%20meeting%20professional%20office%20dakar%20senegal%20team%20discussion%20organized&width=700&height=500&seq=bureau1&orientation=landscape"
                alt="Bureau ASC Sonatel"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/95 rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#E8521A]/10">
                      <i className="ri-calendar-check-line text-[#E8521A]"></i>
                    </div>
                    <div>
                      <div className="font-bold text-[#1A1A1A] text-sm">
                        {meetingLoading ? "Prochaine réunion du bureau" : nextBureauMeeting?.titre ?? "Aucune réunion planifiée"}
                      </div>
                      <div className="text-sm text-gray-400">
                        {meetingLoading ? "Chargement..." : nextBureauMeeting ? formatEventDateTimeRange(nextBureauMeeting) : "L'admin n'a pas encore planifié de réunion."}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm text-gray-500">
                    <div>
                      {meetingLoading ? "Chargement du lieu..." : nextBureauMeeting?.lieu ?? "La prochaine réunion apparaîtra ici automatiquement."}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
